// The signed-in pages (ADR 0023's route table): each one's path, nav label, icon and
// header (the handoff's titles and subtitles, as in the App shell frame story).
import type { IconName } from '@hkdevs/design-system';
import type { Me } from '../api/queries/me';
import type { NavCounts } from '../api/queries/navCounts';
import type { Tenant } from '../api/queries/tenant';

export type PageKey =
  | 'pay'
  | 'payPending'
  | 'paySearch'
  | 'reps'
  | 'repsPending'
  | 'repsSearch'
  | 'people'
  | 'users'
  | 'notices'
  | 'payDataHistory'
  | 'payDataPending'
  | 'products'
  | 'settings'
  | 'operator';

export interface Page {
  path: string;
  title: string;
  subtitle: string;
  /** The nav item's label, when it differs from the title. */
  label?: string;
  icon: IconName;
  /** A page with no nav item of its own: the item shown active on it (Search, A16). */
  navItem?: PageKey;
}

export const PAGES: Record<PageKey, Page> = {
  pay: {
    path: '/pay',
    title: 'Pay History',
    subtitle: 'Your processed checks and statements',
    label: 'History',
    icon: 'check-circle',
  },
  payPending: {
    path: '/pay/pending',
    title: 'Pending pay',
    subtitle: 'Checks awaiting processing',
    label: 'Pending',
    icon: 'calendar',
  },
  // Reached from Pay History, not the nav (A16): the caller's own line items.
  paySearch: {
    path: '/pay/search',
    title: 'Search',
    subtitle: 'Find line items and export them as a PDF',
    icon: 'search',
    navItem: 'pay',
  },
  reps: {
    path: '/reps',
    title: 'My reps',
    subtitle: 'Pay activity across your team',
    label: 'History',
    icon: 'check-circle',
  },
  repsPending: {
    path: '/reps/pending',
    title: 'My reps',
    subtitle: 'Pending pay across your team',
    label: 'Pending',
    icon: 'calendar',
  },
  // Reached from My reps History (A16): the supervisor's team, with a rep filter.
  repsSearch: {
    path: '/reps/search',
    title: 'Search',
    subtitle: 'Find line items across your team and export them as a PDF',
    icon: 'search',
    navItem: 'reps',
  },
  people: {
    path: '/people',
    title: 'People',
    subtitle: 'Reps and supervisors whose pay you can view',
    icon: 'users',
  },
  users: {
    path: '/users',
    title: 'Users',
    subtitle: 'People and access in your workspace',
    icon: 'users',
  },
  notices: {
    path: '/notices',
    title: 'Notices',
    subtitle: 'Messages shown to people when they sign in',
    icon: 'megaphone',
  },
  payDataHistory: {
    path: '/pay-data/history',
    title: 'Pay history uploads',
    subtitle: 'Upload and manage pay history data',
    label: 'Pay History',
    icon: 'check-circle',
  },
  payDataPending: {
    path: '/pay-data/pending',
    title: 'Pending pay uploads',
    subtitle: 'Data for the next upcoming pay date',
    label: 'Pending Pay',
    icon: 'calendar',
  },
  products: {
    path: '/products',
    title: 'Products',
    subtitle: 'Product types and their line-item fields',
    icon: 'package',
  },
  settings: {
    path: '/settings',
    title: 'Settings',
    subtitle: 'White-label and workspace configuration',
    icon: 'settings',
  },
  // Operators only (ADR 0016's A25 amendment): anyone else gets 404.
  operator: {
    path: '/operator',
    title: 'Operator',
    subtitle: 'How this workspace’s app is doing: health, sign-ins and changes',
    icon: 'info',
  },
};

/** The page a path belongs to: the longest page path it equals or sits under. */
export function pageAt(pathname: string): PageKey | null {
  let found: PageKey | null = null;
  for (const [key, page] of Object.entries(PAGES) as [PageKey, Page][]) {
    const under = pathname === page.path || pathname.startsWith(`${page.path}/`);
    if (under && (!found || page.path.length > PAGES[found].path.length)) found = key;
  }
  return found;
}

/** The nav item shown active at a path: its page's, or the one it sits under (Search). */
export function navItemAt(pathname: string): PageKey | null {
  const key = pageAt(pathname);
  return key && (PAGES[key].navItem ?? key);
}

export interface NavItem {
  key: PageKey;
  count?: number | undefined;
}

export interface NavGroup {
  label: string;
  icon: IconName;
  items: NavItem[];
}

export type NavEntry = NavItem | NavGroup;

type Section = Me['sections'][number];
type Feature = Tenant['features'][number];

/**
 * The sidebar for the union of the user's sections (ADR 0016, B9), in the frame story's
 * order, without the pages of disabled features. Counts are left out while loading and
 * when zero.
 */
export function navFor(
  sections: readonly Section[],
  features: readonly Feature[],
  counts: NavCounts | undefined,
): NavEntry[] {
  const has = (section: Section) => sections.includes(section);
  const on = (feature: Feature) => features.includes(feature);
  const entries: NavEntry[] = [];
  if (has('pay')) {
    entries.push({
      label: 'My Pay',
      icon: 'credit-card',
      items: [
        { key: 'pay' },
        ...(has('pendingPay')
          ? [{ key: 'payPending' as const, count: counts?.myPending ?? undefined }]
          : []),
      ],
    });
  }
  if (has('reps')) {
    entries.push({
      label: 'My Reps',
      icon: 'users',
      items: [{ key: 'reps' }, { key: 'repsPending', count: counts?.teamPending ?? undefined }],
    });
  }
  // No Search item: a sidebar item reads as a global search, so Search is reached from the
  // pay history it searches (A16).
  if (has('people')) entries.push({ key: 'people' });
  if (has('users')) entries.push({ key: 'users' });
  if (has('notices') && on('notices')) entries.push({ key: 'notices' });
  if (has('payData')) {
    entries.push({
      label: 'Pay Data',
      icon: 'credit-card',
      items: [
        { key: 'payDataHistory' },
        { key: 'payDataPending', count: counts?.pendingUploads ?? undefined },
        ...(has('products') && on('products_admin') ? [{ key: 'products' as const }] : []),
      ],
    });
  }
  if (has('settings')) entries.push({ key: 'settings' });
  if (has('operator')) entries.push({ key: 'operator' });
  return entries;
}

/** Every page the nav offers, in order. */
export function navPages(entries: readonly NavEntry[]): PageKey[] {
  return entries.flatMap((entry) => ('items' in entry ? entry.items : [entry])).map((i) => i.key);
}
